import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useI18n } from "@/i18n";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Sun, Heart, Users, TrendingUp } from "lucide-react";

const Spain = () => {
  const { language } = useI18n();
  const seo = getSeoCopy("marketsSpain", language);
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "European Markets", url: "/european-markets" },
    { name: "Spain", url: "/markets/spain" }
  ];

  return (
    <>
      <SeoHead
        route="marketsSpain"
        schema={[{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": seo.h1,
          "description": seo.description,
          "url": `https://www.aureum7trading.com/${language}/markets/spain`
        }, {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": b.name,
            "item": `https://www.aureum7trading.com${b.url}`
          }))
        }]}
      />
      <Navigation />
      
      <main className="min-h-screen bg-gradient-to-b from-background to-background/95">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                  <span className="text-2xl">🇪🇸</span>
                  <span className="text-sm font-medium">Diseño Mediterráneo</span>
                </div>
                
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  {seo.h1}
                </h1>
                
                <p className="text-xl text-muted-foreground mb-8">
                  La calidez del diseño mediterráneo se encuentra con la innovación tecnológica. 
                  Sanitarios inteligentes que reflejan el estilo de vida español contemporáneo.
                </p>

                <div className="flex items-center gap-2 px-6 py-3 bg-card rounded-lg border mb-8 inline-flex">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <span className="font-semibold">2,000-3,000</span>
                  <span className="text-muted-foreground">búsquedas mensuales</span>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg">
                    <LocalizedLink to="/contact">Solicitar Información</LocalizedLink>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <LocalizedLink to="/smart-toilets">Ver Colección</LocalizedLink>
                  </Button>
                </div>
              </div>

              <div className="relative">
                <Card className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Sun className="w-8 h-8 text-primary" />
                    <h3 className="text-2xl font-bold">Filosofía Mediterránea</h3>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <Sun className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Diseño Cálido y Acogedor</p>
                        <p className="text-sm text-muted-foreground">Estética mediterránea luminosa</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Heart className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Enfoque en Relaciones</p>
                        <p className="text-sm text-muted-foreground">Atención personalizada y cercana</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Variaciones Regionales</p>
                        <p className="text-sm text-muted-foreground">Adaptado a preferencias locales</p>
                      </div>
                    </li>
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Mediterranean Design Section */}
        <section className="py-20 px-4 bg-card/30">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Diseño Mediterráneo para el Hogar Español
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Sun className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Luz y Espacio</h3>
                <p className="text-muted-foreground">
                  Diseños que maximizan la sensación de amplitud y luminosidad, 
                  características del estilo mediterráneo español
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Calidez y Confort</h3>
                <p className="text-muted-foreground">
                  Experiencia acogedora que refleja el enfoque español en 
                  el bienestar y la comodidad del hogar
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Atención Personalizada</h3>
                <p className="text-muted-foreground">
                  Servicio cercano y personalizado que valora las relaciones 
                  a largo plazo con nuestros clientes
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Regional Variations Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-12">
              Adaptados a las Preferencias Regionales Españolas
            </h2>
            
            <div className="space-y-8">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Costa Mediterránea</h3>
                <p className="text-muted-foreground">
                  Para las viviendas costeras y resorts de lujo en la Costa del Sol, Costa Blanca 
                  y Baleares, ofrecemos diseños que complementan el estilo de vida mediterráneo 
                  sofisticado con materiales resistentes al clima costero.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Centros Urbanos</h3>
                <p className="text-muted-foreground">
                  En Madrid, Barcelona y otras grandes ciudades, nuestros sanitarios inteligentes 
                  se adaptan a espacios urbanos modernos, combinando tecnología de vanguardia 
                  con diseño contemporáneo elegante.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Propiedades Históricas</h3>
                <p className="text-muted-foreground">
                  Para rehabilitaciones de edificios históricos y propiedades de carácter, 
                  ofrecemos soluciones que respetan la arquitectura tradicional mientras 
                  incorporan comodidades modernas discretamente.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Desarrollos de Lujo</h3>
                <p className="text-muted-foreground">
                  Perfectos para nuevos desarrollos premium y villas de lujo, especialmente 
                  en zonas como Marbella, Ibiza y la Costa Brava, donde la calidad y la 
                  innovación son prioritarias.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Local Presence Section */}
        <section className="py-20 px-4 bg-card/30">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-6">
              Presencia Local en Marbella
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Con sede en Marbella, entendemos las necesidades únicas del mercado español. 
              Nuestro equipo local ofrece atención personalizada, asesoramiento experto y 
              soporte continuo en español.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="p-6">
                <div className="text-xl font-bold text-primary mb-2">Marbella</div>
                <p className="text-muted-foreground">Base comercial en España</p>
              </div>
              <div className="p-6">
                <div className="text-xl font-bold text-primary mb-2">REM7</div>
                <p className="text-muted-foreground">Lanzamiento en España</p>
              </div>
              <div className="p-6">
                <div className="text-xl font-bold text-primary mb-2">B2B</div>
                <p className="text-muted-foreground">Precios para proyectos bajo solicitud</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Descubre la Elegancia Mediterránea con Tecnología Inteligente
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contacta con nuestro equipo en Marbella para tu vivienda, hotel o promoción
            </p>
            <Button asChild size="lg">
              <LocalizedLink to="/contact?intent=trade#enquiry-form">Contactar Equipo Español</LocalizedLink>
            </Button>
            <p className="mt-6 text-sm text-muted-foreground">
              ¿Comparando opciones? Lee nuestra{" "}
              <LocalizedLink to="/blog/inodoro-inteligente-guia" className="text-primary underline underline-offset-4">
                guía para elegir un inodoro inteligente en España
              </LocalizedLink>
              .
            </p>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default Spain;